/**
 * TypeSelector
 * Searchable single-select dropdown with chip display for resource type.
 * Matches the legacy Strapi "Add relation" pattern (G3-1).
 * Clicking the input always opens the dropdown; selected option is highlighted.
 */

import { useState, useRef, useEffect } from 'react';
import { Search, X, ChevronDown } from 'lucide-react';

type OptionItem = { id: string; name: string; isActive?: boolean };

type TypeSelectorProps = {
  value: string;
  options: OptionItem[];
  onChange: (typeId: string) => void;
  inactiveSelectedType?: { id: string; name: string };
  error?: string;
};

export function TypeSelector({
  value,
  options,
  onChange,
  inactiveSelectedType,
  error,
}: TypeSelectorProps) {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find((o) => o.id === value);
  const isShowingInactive = inactiveSelectedType && value === inactiveSelectedType.id;
  const displayName = isShowingInactive
    ? `${inactiveSelectedType.name} (inactive)`
    : selectedOption?.name || '';

  const filtered = search.trim()
    ? options.filter((o) => o.name.toLowerCase().includes(search.toLowerCase()))
    : options;

  const handleSelect = (id: string) => {
    onChange(id);
    setSearch('');
    setIsOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setSearch('');
    inputRef.current?.focus();
  };

  return (
    <div ref={containerRef}>
      <span className="block text-sm font-medium text-gray-700">Type *</span>

      <div className="relative mt-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          ref={inputRef}
          type="text"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setIsOpen(true); }}
          onFocus={() => setIsOpen(true)}
          className="block w-full rounded-md border border-gray-300 bg-white pl-9 pr-16 py-2 text-sm shadow-sm focus:border-[#026857] focus:ring-1 focus:ring-[#026857]"
          placeholder={displayName || 'Search types...'}
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
          {value && !isShowingInactive && (
            <button
              type="button"
              onClick={handleClear}
              className="p-0.5 hover:bg-gray-100 rounded"
              aria-label="Remove type"
            >
              <X className="h-3.5 w-3.5 text-gray-400" />
            </button>
          )}
          <ChevronDown className="h-4 w-4 text-gray-400 pointer-events-none" />
        </div>
      </div>

      {isOpen && filtered.length > 0 && (
        <div className="relative">
          <div className="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
            {filtered.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => handleSelect(opt.id)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 truncate ${
                  opt.id === value ? 'bg-[#026857]/5 font-medium text-[#026857]' : ''
                }`}
              >
                {opt.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {isOpen && search.trim() && filtered.length === 0 && (
        <div className="relative">
          <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg p-3 text-sm text-gray-500">
            No types found
          </div>
        </div>
      )}

      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}
